// Excel sheets for the Sources & Uses tabs that the export did not have yet: Fee,
// Facility fee, Marketing, Repayment vectors, and Configuration. Each one writes
// the same numbers the tab shows.

import { formatMonthLabel, forecastStartMonth } from './cashflowFeeExposureHelpers';
import { lenderLabel, normalizeCompanyCashInputs, withLenders } from './companyCash';
import { buildFacilityFeeRows } from './facilityFeeSchedule';
import { FEE_METHODS, buildFeeRows } from './feeCalculation';
import { MARKETING_SOURCES, marketingUsedModel } from './MarketingUsedTable';
import { shownPipelineClient } from './pipelineExposure';
import { REPAYMENT_VECTOR_SOURCES, repaymentVectorModel } from './repaymentVectors';

const MONEY = '#,##0;(#,##0);-';
const PERCENT = '0.0"%"';
const NAVY = 'FF1F4E79';
const WHITE = 'FFFFFFFF';
const TOTAL_FILL = 'FFD6E4F0';

function fill(argb) {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb } };
}

function argb(hex) {
  return `FF${String(hex || '').replace('#', '').toUpperCase()}`;
}

function title(sheet, text, note) {
  sheet.getCell(1, 1).value = text;
  sheet.getCell(1, 1).font = { bold: true, size: 12 };
  if (note) {
    sheet.getCell(2, 1).value = note;
    sheet.getCell(2, 1).font = { italic: true, color: { argb: 'FF595959' } };
  }
}

function headerRow(sheet, rowNumber, values) {
  values.forEach((value, index) => {
    const cell = sheet.getCell(rowNumber, index + 1);
    cell.value = value;
    cell.fill = fill(NAVY);
    cell.font = { bold: true, color: { argb: WHITE } };
  });
}

function sectionRow(sheet, rowNumber, text, width = 8) {
  for (let column = 1; column <= width; column += 1) {
    const cell = sheet.getCell(rowNumber, column);
    if (column === 1) cell.value = text;
    cell.fill = fill(NAVY);
    cell.font = { bold: true, color: { argb: WHITE } };
  }
}

function monthHeaders(sheet, rowNumber, months, firstColumn) {
  const forecast = forecastStartMonth();
  months.forEach((month, index) => {
    const cell = sheet.getCell(rowNumber, firstColumn + index);
    cell.value = formatMonthLabel(month);
    cell.fill = fill(month < forecast ? 'FF70AD47' : 'FF2E75B6');
    cell.font = { bold: true, color: { argb: WHITE } };
    cell.alignment = { horizontal: 'center' };
  });
}

function widths(sheet, first, count, rest = 13) {
  sheet.getColumn(1).width = first;
  for (let column = 2; column <= count + 1; column += 1) sheet.getColumn(column).width = rest;
}

// writeAmountTable writes rows of { label, values } under month headers; the last
// row is bold when it is the total.
function writeAmountTable(sheet, startRow, months, rows) {
  monthHeaders(sheet, startRow, months, 2);
  sheet.getCell(startRow, 1).value = 'Customer';
  sheet.getCell(startRow, 1).font = { bold: true };
  rows.forEach((row, index) => {
    const rowNumber = startRow + 1 + index;
    const total = row.id === 'total';
    sheet.getCell(rowNumber, 1).value = row.label;
    if (total) sheet.getCell(rowNumber, 1).font = { bold: true };
    row.values.forEach((value, column) => {
      const cell = sheet.getCell(rowNumber, 2 + column);
      if (value) cell.value = value;
      cell.numFmt = MONEY;
      if (total) {
        cell.font = { bold: true };
        cell.fill = fill(TOTAL_FILL);
      }
    });
  });
  widths(sheet, 30, months.length);
}

export function writeFeeSheet(sheet, { customers, months, method }) {
  const label = FEE_METHODS.find((item) => item.id === method)?.label || method;
  title(sheet, 'Fee', `Fee calculation: ${label}`);
  writeAmountTable(sheet, 4, months, buildFeeRows(customers, months, method));
}

export function writeFacilityFeeSheet(sheet, { customers, dealTerms, months }) {
  title(
    sheet,
    'Facility fee',
    'Live customers only. Each customer’s facility fees are added up and split equally across the commitment months on their deal. A deal without a commitment shows the fees in the months they are charged.'
  );
  if (!months.length) {
    sheet.getCell(4, 1).value = 'These live customers have no commitment length on their deal.';
    return;
  }
  writeAmountTable(sheet, 4, months, buildFacilityFeeRows(customers, dealTerms, months));
}

// writeMarketingSheet: the marketing behind each projected funding, coloured by where it came from.
export function writeMarketingSheet(sheet, { customers, pipelineClients }) {
  const model = marketingUsedModel(customers, pipelineClients);
  title(sheet, 'Marketing', 'The marketing each projected funding is sized from, by marketing month. Coloured cells were generated by the calculation.');
  let rowNumber = 3;
  Object.values(MARKETING_SOURCES).forEach((source) => {
    const cell = sheet.getCell(rowNumber, 1);
    cell.value = source.label;
    cell.fill = fill(argb(source.background));
    if (source.color) cell.font = { color: { argb: argb(source.color) } };
    rowNumber += 1;
  });
  rowNumber += 1;
  const header = rowNumber;
  sheet.getCell(header, 1).value = 'Customer';
  sheet.getCell(header, 2).value = 'Stage';
  [1, 2].forEach((column) => { sheet.getCell(header, column).font = { bold: true }; });
  monthHeaders(sheet, header, model.months, 3);
  model.rows.forEach((row, index) => {
    const r = header + 1 + index;
    sheet.getCell(r, 1).value = row.name;
    sheet.getCell(r, 2).value = row.stage;
    model.months.forEach((month, column) => {
      const item = row.byMonth[month];
      if (!item) return;
      const source = MARKETING_SOURCES[item.source] || MARKETING_SOURCES.saved;
      const cell = sheet.getCell(r, 3 + column);
      cell.value = Number(item.spend) || 0;
      cell.numFmt = MONEY;
      if (item.source && item.source !== 'saved') {
        cell.fill = fill(argb(source.background));
        if (source.color) cell.font = { color: { argb: argb(source.color) } };
      }
      cell.note = source.label;
    });
  });
  const totalRow = header + 1 + model.rows.length;
  const generatedRow = totalRow + 1;
  sheet.getCell(totalRow, 1).value = 'Total';
  sheet.getCell(generatedRow, 1).value = 'of which generated';
  model.totals.forEach((value, column) => {
    const total = sheet.getCell(totalRow, 3 + column);
    total.value = value;
    total.numFmt = MONEY;
    total.font = { bold: true };
    total.fill = fill(TOTAL_FILL);
    const generated = sheet.getCell(generatedRow, 3 + column);
    generated.value = model.generated[column];
    generated.numFmt = MONEY;
  });
  sheet.getColumn(1).width = 30;
  sheet.getColumn(2).width = 16;
  for (let column = 3; column < 3 + model.months.length; column += 1) sheet.getColumn(column).width = 13;
}

export function writeRepaymentVectorsSheet(sheet, { customers, pipelineClients, settings }) {
  const model = repaymentVectorModel(customers, pipelineClients, settings);
  title(
    sheet,
    'Repayment vectors',
    'Cumulative % of each projected funding repaid by the end of each repayment month (M1 = first month money comes back). Grey = past the end of the curve, where its last monthly step repeats.'
  );
  const header = 4;
  headerRow(sheet, header, ['Customer', 'Stage', 'Vector', 'Revenue margin', ...Array.from({ length: model.months }, (_, i) => `M${i + 1}`)]);
  model.rows.forEach((row, index) => {
    const r = header + 1 + index;
    sheet.getCell(r, 1).value = row.name;
    sheet.getCell(r, 2).value = row.stage;
    sheet.getCell(r, 3).value = REPAYMENT_VECTOR_SOURCES[row.source];
    if (row.margin) sheet.getCell(r, 4).value = row.margin;
    row.cumulative.forEach((value, column) => {
      const cell = sheet.getCell(r, 5 + column);
      cell.value = value;
      cell.numFmt = PERCENT;
      if (column >= row.curveLength) cell.font = { color: { argb: 'FFA0AEC0' } };
    });
  });
  if (model.liveWithout.length) {
    sheet.getCell(header + 2 + model.rows.length, 1).value = `No repayment vector saved for ${model.liveWithout.join(', ')}.`;
  }
  sheet.getColumn(1).width = 30;
  sheet.getColumn(2).width = 16;
  sheet.getColumn(3).width = 30;
  sheet.getColumn(4).width = 14;
}

function percentOrBlank(fraction) {
  if (fraction === null || fraction === undefined || fraction === '') return '';
  const number = Number(fraction);
  if (!Number.isFinite(number)) return '';
  return number > 1 ? number : number * 100;
}

function vectorText(points) {
  return (points || [])
    .filter((point) => Number.isInteger(Number(point?.month)))
    .map((point) => `M${point.month} ${point.cumulativeRoasPct}%`)
    .join(', ');
}

function marketingText(rows) {
  return (rows || [])
    .filter((row) => row?.month && Number(row.spend) > 0)
    .map((row) => `${row.month}: ${Number(row.spend).toLocaleString('en-US')}`)
    .join(', ');
}

function writeRows(sheet, startRow, header, rows) {
  headerRow(sheet, startRow, header);
  rows.forEach((values, index) => {
    values.forEach((value, column) => {
      const cell = sheet.getCell(startRow + 1 + index, column + 1);
      cell.value = value === undefined ? '' : value;
    });
  });
  return startRow + 1 + rows.length + 1;
}

// writeConfigurationSheet: everything entered on Configuration, as saved.
export function writeConfigurationSheet(sheet, {
  viewName,
  tenant,
  startMonth,
  endMonth,
  settings,
  liveCustomers,
  pipelineClients,
  companyCash,
  userName = (id) => id,
}) {
  title(sheet, 'Configuration', viewName);
  const s = settings || {};
  let row = 3;
  sectionRow(sheet, row, 'GENERAL');
  row += 1;
  const general = [
    ['From', startMonth ? formatMonthLabel(startMonth) : ''],
    ['Through', endMonth ? formatMonthLabel(endMonth) : ''],
    ['Extend funding', s.extendFunding === false ? 'No' : 'Yes'],
    ['Last funding month (saved)', s.lastFundingMonth ? formatMonthLabel(s.lastFundingMonth) : ''],
    ['Default marketing spend (first month)', Number(s.defaultMarketingSpend) || 0],
    ['Default marketing growth % per month', percentOrBlank(s.defaultMarketingGrowth)],
    ['Generic curve', vectorText(s.genericVector)],
    ['Last run', s.lastRunAt ? `${new Date(s.lastRunAt).toLocaleString('en-US')}${s.lastRunBy ? ` by ${userName(s.lastRunBy)}` : ''}` : ''],
  ];
  general.forEach(([label, value]) => {
    sheet.getCell(row, 1).value = label;
    sheet.getCell(row, 2).value = value;
    if (typeof value === 'number' && label.startsWith('Default marketing spend')) sheet.getCell(row, 2).numFmt = MONEY;
    row += 1;
  });
  row += 1;

  sectionRow(sheet, row, 'LIVE CUSTOMERS', 9);
  row += 1;
  row = writeRows(sheet, row, [
    'Customer', 'Commitment (months)', 'Commitment date', 'First installment started', 'Last installment',
    'Last installment counted from', 'Marketing growth % (empty = default)', 'Saved marketing months',
  ], (liveCustomers || []).map((client) => [
    client.companyName || client.customerId,
    client.commitmentLength || '',
    client.commitmentDate || '',
    client.firstInstallmentStarted || '',
    client.lastInstallment || '',
    client.lastInstallmentFrom === 'commitment_date' ? 'Commitment date' : client.lastInstallmentFrom ? 'First installment' : '',
    percentOrBlank(client.marketingGrowth),
    (client.savedMarketingMonths || []).length,
  ]));

  const shown = (pipelineClients || []).filter((client) => shownPipelineClient(client, tenant));
  const pipelineHeader = [
    'Customer', 'Stage', 'Included', 'First cohort funded', 'Fund %', 'Cohorts', 'Lag (months)', 'Monthly IRR %',
    'Max cap', 'Max exposure', 'Generic curve', 'Curve multiplier', 'Repayment vector', 'Marketing',
  ];
  const pipelineRow = (client) => [
    client.companyName || client.customerId,
    client.future ? 'Future' : client.stage || '',
    client.included ? 'Yes' : 'No',
    client.firstFundingMonth ? formatMonthLabel(client.firstFundingMonth) : '',
    percentOrBlank(client.fundingPct),
    client.cohorts ?? '',
    client.lagMonths ?? '',
    percentOrBlank(client.monthlyIrr),
    client.maxCap ?? '',
    client.maxExposure ?? '',
    client.useGenericCurve ? 'Yes' : 'No',
    client.curveMultiplier ?? '',
    vectorText(client.repaymentVector),
    marketingText(client.marketing),
  ];
  sectionRow(sheet, row, 'NON LIVE CUSTOMERS', pipelineHeader.length);
  row += 1;
  row = writeRows(sheet, row, pipelineHeader, shown.filter((client) => !client.future).map(pipelineRow));
  sectionRow(sheet, row, 'FUTURE CUSTOMERS', pipelineHeader.length);
  row += 1;
  row = writeRows(sheet, row, pipelineHeader, shown.filter((client) => client.future).map(pipelineRow));

  const cash = normalizeCompanyCashInputs(withLenders(companyCash || {}));
  sectionRow(sheet, row, 'COMPANY CASH');
  row += 1;
  [
    ['Opening cash', cash.openingCash],
    ['Minimum close balance', cash.minCloseBalance],
    ['Operating expenses per month', cash.monthlyOpex],
    ['Capital fund', cash.capitalAmount],
    ['Capital arrives', cash.capitalMonth ? formatMonthLabel(cash.capitalMonth) : ''],
    ['AR facility cap % of next month', cash.arCapPct],
    ['AR facility fee - annual IRR %', cash.arRate],
    ['AR facility fee - monthly rate %', Math.round(cash.arMonthlyRate * 10000) / 10000],
  ].forEach(([label, value]) => {
    sheet.getCell(row, 1).value = label;
    sheet.getCell(row, 2).value = value;
    row += 1;
  });
  row += 1;
  writeRows(sheet, row, ['Lender', 'Name', 'Cap', 'Annual IRR %', 'Monthly rate %', 'Start month'], cash.lenders.map((lender, index) => [
    lenderLabel(lender, index),
    lender.name,
    lender.cap,
    lender.rate,
    Math.round(lender.monthlyRate * 10000) / 10000,
    lender.startMonth ? formatMonthLabel(lender.startMonth) : 'From the start',
  ]));

  sheet.getColumn(1).width = 38;
  for (let column = 2; column <= 14; column += 1) sheet.getColumn(column).width = 18;
  sheet.getColumn(13).width = 60;
  sheet.getColumn(14).width = 60;
}

